import { useState } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, useFormContext } from "react-hook-form";

import { colors } from "../../../../theme/palette";
import APP_ROUTES from "../../../../routers/routeConstants";
import { BackArrowIcon } from "../../../../assets/icons/MUI/PaymentIcons";
import { ActiveStep, MinimumServicePrices } from "../../payment.types";

import CreatePaymentLinkStepper from "./CreatePaymentLinkStepper";
import { CreatePaymentLinkProvider } from "./Steps/CreatePaymentLinkProvider";
import PaymentDetails from "./Steps/PaymentDetails";
import SelectRecipients from "./Steps/SelectRecipients";
import RegisterFormFields from "./Steps/RegisterFormFields/RegisterFormFields";
import ReviewPayment from "./Steps/ReviewPayment";
import ShareMagicLink from "./ShareMagicLink/ShareMagicLink";
import { useCreatePaymentLink } from "../../../../api/queries/payment/paymentMutations";
import { useMinimumServicePrices } from "../../../../api/queries/payment/paymentQueries";

const CreatePaymentLink = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<ActiveStep>("paymentDetails");
  const [magicLink, setMagicLink] = useState("");
  const [searchParams] = useSearchParams();
  const countryShort = searchParams.get("country");

  const { handleSubmit, trigger, setError, watch } = useFormContext();

  const handleCreatingPaymentLinkSuccessfully = (link: string) => {
    setMagicLink(link);
    setStep("magicLink");
  };
  const { mutate, isPending } = useCreatePaymentLink(handleCreatingPaymentLinkSuccessfully, () =>
    console.error("Mutation failed")
  );

  const onSubmit = (data: any) => {
    mutate({ ...data, paymentMethod: countryShort === "eg" ? "paymob" : "stripe" });
  };

  // const { minimumServicePrices } = useMinimumServicePrices();
  // const amount = watch("amount");
  // const currency = watch("currency");

  const handleNext = async () => {
    if (step === "paymentDetails") {
      // if (!minimumServicePrices) return;
      // if (Number(amount) < minimumServicePrices[currency as keyof MinimumServicePrices]) {
      //   trigger(["paymentCategory", "paymentFor", "expiryDate"]);
      //   setError("amount", {
      //     message: t("pages.payment.createPaymentLink.amountLess", {
      //       amount: minimumServicePrices[currency as keyof MinimumServicePrices],
      //     }),
      //   });
      //   return;
      // }
      if (await trigger(["amount", "currency", "paymentCategory", "paymentFor", "expiryDate"])) {
        setStep("selectRecipients");
      }
    }
    if (step === "selectRecipients") {
      if (await trigger(["recipientsType", "recipientsOutSideGoalngo"])) {
        setStep("registerForm");
      }
    }
    if (step === "registerForm") {
      if (await trigger(["registerFormFields"])) {
        setStep("reviewPayment");
      }
    }
  };

  const handleBack = () => {
    if (step === "selectRecipients") setStep("paymentDetails");
    if (step === "registerForm") setStep("selectRecipients");
    if (step === "reviewPayment") setStep("registerForm");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", columnGap: "8px", marginBottom: "24px" }}>
        <IconButton
          onClick={() => navigate(APP_ROUTES.PAYMENT)}
          sx={{
            width: "fit-content",
            height: "fit-content",
          }}
        >
          <BackArrowIcon />
        </IconButton>
        <Box>
          <Typography
            component="h2"
            sx={{
              color: "#1C1C1C",
              fontFamily: "Montserrat",
              fontSize: "20px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "30px",
            }}
          >
            {t("pages.payment.createPaymentLink.title")}
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "#111",
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "20px",
            }}
          >
            {t("pages.payment.createPaymentLink.description")}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          padding: "24px 0",
          background: colors.White,
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          rowGap: "24px",
          position: "relative",
        }}
      >
        {step === "magicLink" && magicLink && magicLink?.length > 0 ? (
          <ShareMagicLink magicLink={magicLink} />
        ) : (
          <>
            <Box sx={{ marginBottom: "24px", padding: "0 24px" }}>
              <Typography
                component="h2"
                sx={{
                  color: "#1C1C1C",
                  fontFamily: "Montserrat",
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "30px",
                  textAlign: "center",
                }}
              >
                {t(`pages.payment.createPaymentLink.${step}`)}
              </Typography>
              {step === "registerForm" && (
                <Typography
                  component="p"
                  sx={{
                    color: "var(--Gray-600, #475467)",
                    fontFamily: "Montserrat",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "24px",
                    textAlign: "center",
                  }}
                >
                  {t("pages.payment.createPaymentLink.registerFormDescription")}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", columnGap: "64px", padding: "0 24px" }}>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <CreatePaymentLinkStepper activeStep={step} setActiveStep={setStep} />
              </Box>
              {step === "paymentDetails" && <PaymentDetails />}
              {step === "selectRecipients" && <SelectRecipients />}
              {step === "registerForm" && <RegisterFormFields />}
              {step === "reviewPayment" && <ReviewPayment />}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                columnGap: "12px",
                padding: "12px 24px 0",
                borderTop: "1px solid #EAECF0",
              }}
            >
              {step !== "paymentDetails" && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                  sx={{ width: { xs: "100%", md: "fit-content" } }}
                >
                  {t("pages.payment.createPaymentLink.back")}
                </Button>
              )}
              {step === "reviewPayment" ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isPending}
                  sx={{ width: { xs: "100%", md: "fit-content" } }}
                >
                  {t("pages.payment.createPaymentLink.generateLink")}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext} sx={{ width: { xs: "100%", md: "fit-content" } }}>
                  {t("pages.payment.createPaymentLink.continue")}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default () => (
  <CreatePaymentLinkProvider {...useForm()}>
    <CreatePaymentLink />
  </CreatePaymentLinkProvider>
);
